import type { Json } from "@/infrastructure/supabase/database.types";
import { STAGES, type Stage } from "@/presentation/data/architecture-components";
import type {
  ArchitectureMapEdge,
  ArchitectureMapEdgeKind,
  ArchitectureMapNode,
  ArchitectureMapNodeKind,
  MapDomain,
} from "@/presentation/data/architecture-map-data";
import {
  type B2bUsersEdge,
  type B2bUsersEdgeKind,
  type B2bUsersNode,
  type B2bUsersNodeKind,
} from "@/presentation/data/b2b-users-stage-seed";
import { isDomain } from "@/presentation/data/domains";

export type WorkspacePosition = { x: number; y: number };
export type WorkspaceStageHighlights = Partial<Record<Stage, string[]>>;

export function parseWorkspacePositions(json: Json | undefined): Record<string, WorkspacePosition> {
  if (json === null || json === undefined || typeof json !== "object" || Array.isArray(json)) {
    return {};
  }
  const out: Record<string, WorkspacePosition> = {};
  for (const [id, v] of Object.entries(json as Record<string, unknown>)) {
    if (!v || typeof v !== "object" || Array.isArray(v)) continue;
    const rec = v as Record<string, unknown>;
    const x = Number(rec.x);
    const y = Number(rec.y);
    if (Number.isFinite(x) && Number.isFinite(y)) out[id] = { x, y };
  }
  return out;
}

export function parseWorkspaceStageHighlights(json: Json | undefined): WorkspaceStageHighlights {
  if (json === null || json === undefined || typeof json !== "object" || Array.isArray(json)) {
    return {};
  }
  const out: WorkspaceStageHighlights = {};
  for (const k of STAGES) {
    const v = (json as Record<string, unknown>)[k];
    if (Array.isArray(v)) {
      out[k] = v.filter((x): x is string => typeof x === "string");
    }
  }
  return out;
}

export function positionsToJson(pos: Record<string, WorkspacePosition>): Json {
  return pos as unknown as Json;
}

export function stageHighlightsToJson(h: WorkspaceStageHighlights): Json {
  return h as unknown as Json;
}

const B2B_EDGE_KINDS: readonly B2bUsersEdgeKind[] = [
  "primary",
  "manual",
  "bidirectional",
  "dependency",
  "finance",
  "feedback",
];

const B2B_NODE_KINDS: readonly B2bUsersNodeKind[] = ["system", "actor"];

function parseString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function parseFiniteNumber(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function parseB2bUsersNodes(json: Json | undefined): B2bUsersNode[] {
  if (!Array.isArray(json)) return [];
  const out: B2bUsersNode[] = [];
  for (const entry of json) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const r = entry as Record<string, unknown>;
    const id = parseString(r.id);
    const label = parseString(r.label);
    const role = parseString(r.role) ?? "";
    const domain = parseString(r.domain);
    const kindRaw = parseString(r.kind);
    const x = parseFiniteNumber(r.x);
    const y = parseFiniteNumber(r.y);
    if (!id || !label || x === null || y === null) continue;
    if (!domain || !isDomain(domain)) continue;
    const kind = B2B_NODE_KINDS.find((k) => k === kindRaw) ?? "system";
    out.push({ id, label, role, domain, kind, x, y });
  }
  return out;
}

export function parseB2bUsersEdges(json: Json | undefined): B2bUsersEdge[] {
  if (!Array.isArray(json)) return [];
  const out: B2bUsersEdge[] = [];
  for (const entry of json) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const r = entry as Record<string, unknown>;
    const id = parseString(r.id);
    const source = parseString(r.source);
    const target = parseString(r.target);
    const kindRaw = parseString(r.kind);
    const label = parseString(r.label) ?? undefined;
    if (!id || !source || !target) continue;
    const kind = B2B_EDGE_KINDS.find((k) => k === kindRaw) ?? "primary";
    out.push({ id, source, target, kind, label });
  }
  return out;
}

export function b2bUsersNodesToJson(nodes: readonly B2bUsersNode[]): Json {
  return nodes as unknown as Json;
}

export function b2bUsersEdgesToJson(edges: readonly B2bUsersEdge[]): Json {
  return edges as unknown as Json;
}

export function b2bUsersPositionsToJson(pos: Record<string, WorkspacePosition>): Json {
  return pos as unknown as Json;
}

// ─── Map workspace (b2c / b2b / mvp shared workspace) ─────────────────────

const MAP_EDGE_KINDS: readonly ArchitectureMapEdgeKind[] = [
  "primary",
  "manual",
  "bidirectional",
  "finance",
  "feedback",
  "dependency",
];

const MAP_NODE_KINDS: readonly ArchitectureMapNodeKind[] = ["system", "actor"];

const MAP_DOMAINS_INCL_ACTORS: readonly MapDomain[] = [
  "supply-chain",
  "catalog-content",
  "pricing-commerce",
  "fulfillment-ops",
  "customer-experience",
  "finance",
  "app-core",
  "data-bi",
  "actors",
];

const MAP_OWNERSHIPS = ["yandex", "external"] as const;
type MapOwnership = (typeof MAP_OWNERSHIPS)[number];

function parseStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}

function parseMapDomain(v: unknown): MapDomain | null {
  if (typeof v !== "string") return null;
  return MAP_DOMAINS_INCL_ACTORS.find((d) => d === v) ?? null;
}

function parseOwnership(v: unknown): MapOwnership | null {
  if (typeof v !== "string") return null;
  return MAP_OWNERSHIPS.find((o) => o === v) ?? null;
}

function parseStageArray(v: unknown): Stage[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is Stage => typeof x === "string" && STAGES.includes(x as Stage));
}

export function parseMapNodes(json: Json | undefined): ArchitectureMapNode[] {
  if (!Array.isArray(json)) return [];
  const out: ArchitectureMapNode[] = [];
  for (const entry of json) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const r = entry as Record<string, unknown>;
    const id = parseString(r.id);
    const name = parseString(r.name);
    const role = parseString(r.role) ?? "";
    const domain = parseMapDomain(r.domain);
    const ownership = parseOwnership(r.ownership);
    const kindRaw = parseString(r.kind);
    const pos = r.position as Record<string, unknown> | undefined;
    const x = parseFiniteNumber(pos?.x);
    const y = parseFiniteNumber(pos?.y);
    if (!id || !name || !domain || !ownership || x === null || y === null) continue;
    const kind = MAP_NODE_KINDS.find((k) => k === kindRaw) ?? "system";
    out.push({
      id,
      name,
      role,
      kind,
      ownership,
      domain,
      stages: parseStageArray(r.stages),
      meetings: parseStringArray(r.meetings),
      position: { x, y },
      note: parseString(r.note) ?? undefined,
      modules: Array.isArray(r.modules) ? parseStringArray(r.modules) : undefined,
    });
  }
  return out;
}

export function parseMapEdges(json: Json | undefined): ArchitectureMapEdge[] {
  if (!Array.isArray(json)) return [];
  const out: ArchitectureMapEdge[] = [];
  for (const entry of json) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const r = entry as Record<string, unknown>;
    const id = parseString(r.id);
    const source = parseString(r.source);
    const target = parseString(r.target);
    const kindRaw = parseString(r.kind);
    const label = parseString(r.label) ?? undefined;
    if (!id || !source || !target) continue;
    const kind = MAP_EDGE_KINDS.find((k) => k === kindRaw) ?? "primary";
    out.push({ id, source, target, kind, label });
  }
  return out;
}

export function mapNodesToJson(nodes: readonly ArchitectureMapNode[]): Json {
  return nodes as unknown as Json;
}

export function mapEdgesToJson(edges: readonly ArchitectureMapEdge[]): Json {
  return edges as unknown as Json;
}
